import { useErrorHandler } from 'src/hooks/useErrorHandler'
import React, { useState, useEffect } from 'react'
import {
  Create,
  SimpleForm,
  TextInput,
  BooleanInput,
  useTranslate,
  required,
  useNotify,
  maxLength,
} from 'react-admin'
import { CardCommon } from 'src/components/uiElements'
import { Grid, Skeleton } from '@mui/material'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import { productServices } from 'src/services/reservation/product/product.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { HasPermission } from 'src/helper/HasPermission'
import { FormFiscalData } from '../../administration/business-unit/logic/FormFiscalData'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { dueDateServices } from 'src/services/reservation/due-date/dueDate.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { countryServices } from 'src/services/reservation/country/country.service'

export const SupplierCreate = (props) => {
  const handleCreateError = (error) => {
    const body = error?.body || error
    if (body?.status_code === 418) {
      const detail = body?.data?.errors || body?.data?.data || body?.data
      error.status_code = 418
      error.response = {
        status: 418,
        data: {
          ...body,
          data: { errors: Array.isArray(detail) ? detail : [detail] },
        },
      }
    }
    showError(error)
  }

  const notify = useNotify()

  const showError = useErrorHandler()
  const translate = useTranslate()
  const [productTypes, setProductTypes] = useState([])
  const [loadingProductTypes, setLoadingProductTypes] = useState(false)
  const [currencyList, setCurrencyList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const [dueDateMethodsMap, setDueDateMethodsMap] = useState([])
  const [loadingDueDateMethodMap, setLoadingDueDateMethodMap] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [loadingCountryList, setLoadingCountryList] = useState(false)

  const supplierTypes = [{id:1, name:'Aéreo'}, {id:2, name:'Hotel'}, {id:3, name:'Otros prestadores turísticos'}]
  let searchTimeoutCountry

  useEffect(() => {
    getCurrencies()
    ;(async () => {
      try {
        setLoadingProductTypes(true)
        const response = await productServices.getAllProductTypes()
        setProductTypes(response)
        setLoadingProductTypes(false)
      } catch (error) {
        notify(`product types not found`, { type: 'error' })
        setLoadingProductTypes(false)
        return null
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const getCountry = async (long_name) => {
    clearTimeout(searchTimeoutCountry)

    searchTimeoutCountry = setTimeout(async () => {
      try {
        setLoadingCountryList(true)
        const allCountries = await countryServices.getByAll({ long_name: long_name })
        setCountryList(allCountries)
        setLoadingCountryList(false)
      } catch (error) {
        setCountryList([])
        setLoadingCountryList(false)
      }
    }, 700)
  }

  const getDueDateMethod = async (name) => {
    if (name?.length < 3) {
      setDueDateMethodsMap([])
      return
    }
    try {
      setLoadingDueDateMethodMap(true)
      const dueDateRange = await dueDateServices.getByAllDueDateMethod({ name__ilike: name })
      setDueDateMethodsMap(dueDateRange)
    } catch (error) {
      setDueDateMethodsMap([])
    } finally {
      setLoadingDueDateMethodMap(false)
    }
  }

  return (
    <Create
            {...props}
            redirect='list'
            actions={null}
            mutationOptions={{ onError: handleCreateError }}
          >
      <CardCommon title={translate('ra.title.create_supplier')} actions={null}>
        <SimpleForm>
          <CardCommon title={translate('ra.title.basic_data')} actions={null} style={{ width: '100%' }}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.name')} required md={6} lg={3}>
                <TextInput validate={required()} label={false} fullWidth source='name' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.code')} required md={6} lg={3}>
                <TextInput label={false} fullWidth source='code' validate={[required()]} />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.currency')} required md={6} lg={3}>
                {loadingCurrencyList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    size='small'
                    optionsText={['short_name']}
                    fullWidth
                    label=''
                    source='currency_id'
                    options={currencyList}
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.country')} md={6} lg={3}>
                <AutocompleteInput
                  source='country_id'
                  options={countryList}
                  loading={loadingCountryList}
                  optionsText={['long_name']}
                  optionValue='id'
                  onSearch={getCountry}
                  resetOnLoad={false}
                  useOptionValue={true}
                  positionOptionsText={0}
                  useOptionValueByOptions
                />
              </EditInputsWrap>
            </Grid>
          </CardCommon>

          <HasPermission permission='fiscal_data' resource={'supplier'}>
            <CardCommon title={translate('ra.title.fiscal_data')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
              <FormFiscalData isSupplier={true}/>
            </CardCommon>
          </HasPermission>

          <CardCommon title={translate('ra.title.contact')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.email')}>
                <TextInput source='email' label='' fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.phone')}>
                <NumberInput source='phone' label='' fullWidth />
              </EditInputsWrap>
            </Grid>
          </CardCommon>

          <CardCommon title={translate('ra.title.configuration')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.due_date_method')} required className='small'>
                <AutocompleteInput
                  source={`due_date_method_id`}
                  options={dueDateMethodsMap}
                  loading={loadingDueDateMethodMap}
                  optionsText={['name']}
                  useOptionValue
                  optionValue='id'
                  onSearch={(name) => getDueDateMethod(name)}
                  resetOnLoad={true}
                  fullWidth
                  validate={required()}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.product-types')} required>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='product_type_id'
                    optionsText={['long_name']}
                    optionValue='id'
                    options={productTypes}
                    label=''
                    fullWidth
                    multiple
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.gmx_contract_id')}>
                <NumberInput label={false} fullWidth source='gmx_contract_id' />
              </EditInputsWrap>
              {/* <EditInputsWrap tooltiptext={'Longitud maxima: 10 caracteres'} label={translate('ra.field.tma_code')} required>
                <TextInput validate={[required(), maxLength(10)]} label={false} fullWidth source='billing_code' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.is_active')}>
                <BooleanInput label={false} fullWidth source='is_active' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.vat_active')}>
                <BooleanInput label={false} fullWidth source='vat_active' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.supplier_type')}>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='supplier_type'
                    optionsText={['name']}
                    optionValue='id'
                    options={supplierTypes}
                    label=''
                    fullWidth
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap> */}
            </Grid>
          </CardCommon>
        </SimpleForm>
      </CardCommon>
    </Create>
  )
}